import { ExternalServiceId } from '@edgebox/data-definition-kit';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { IResolvedSuggestionTerm } from './SuggestedFaqs';

/**
 * Reading a name once per distinct key and keeping it for the session: a taxonomy
 * term for every screen that shows a term by name rather than by key, and, on the
 * same reader, anything else a screen names by a key it holds.
 */

/**
 * Reads `load` at most once per key and keeps what it answered, so a screen that
 * asks for the same key again — on this render or after it was reopened — puts
 * nothing further on the wire. Two callers asking while one read is in flight
 * share that read. Only an answer is kept: a key that could not be read is asked
 * for again next time, so a name that was briefly unreachable is not missing for
 * the rest of the session.
 */
export function readOncePerKey<T>({ caches }: { caches: IReadOncePerKeyCaches<T> }) {
  return async ({ key, load }: { key: string; load: () => Promise<T | undefined> }): Promise<T | undefined> => {
    if (caches.byKey.has(key)) {
      return caches.byKey.get(key);
    }

    const pending = caches.inFlight.get(key);
    if (pending) {
      return pending;
    }

    const request = load()
      .catch(() => undefined)
      .finally(() => {
        caches.inFlight.delete(key);
      });

    caches.inFlight.set(key, request);

    const loaded = await request;
    if (loaded) {
      caches.byKey.set(key, loaded);
    }
    return loaded;
  };
}

/** The pair of maps one such reader keeps: what was read, and what is being read. */
export interface IReadOncePerKeyCaches<T> {
  byKey: Map<string, T | undefined>;
  inFlight: Map<string, Promise<T | undefined>>;
}

/** Builds the pair a reader needs, so a caller never declares the two maps itself. */
export function readOncePerKeyCaches<T>(): IReadOncePerKeyCaches<T> {
  return { byKey: new Map(), inFlight: new Map() };
}

const readTermOncePerKey = readOncePerKey<IResolvedSuggestionTerm>({ caches: readOncePerKeyCaches<IResolvedSuggestionTerm>() });

/**
 * Loads a taxonomy term once per distinct key and keeps the loaded term, not
 * only its name: the Authority topic badge of the FAQ tab places a term by its
 * facet, which only the term itself carries. A term that cannot be read answers
 * `undefined`, so a caller that must not show a raw key can tell "not resolved"
 * from "resolved to a name".
 */
export async function loadTaxonomyTerm(api: ISyncCoreApi | undefined, termKey: string): Promise<IResolvedSuggestionTerm | undefined> {
  const cacheKey = termKey.trim();
  if (!api || !cacheKey) {
    return undefined;
  }

  return readTermOncePerKey({
    key: cacheKey,
    load: async () => {
      const term = await api.service.taxonomyTerms.itemByKey(cacheKey as ExternalServiceId);
      const name = term?.name?.trim();
      return name ? { name, facet: term?.taxonomy?.facet } : undefined;
    },
  });
}

/**
 * The name to print for a term key. Every caller that had this helper before
 * keeps its behaviour: an unreadable term prints its key rather than nothing.
 */
export async function loadTaxonomyTermText(api: ISyncCoreApi | undefined, termKey: string): Promise<string> {
  if (!api || !termKey.trim()) {
    return termKey || '-';
  }

  const loaded = await loadTaxonomyTerm(api, termKey);
  return loaded?.name || termKey;
}
